<template>
	<v-autocomplete
		v-model="fieldValue"
		ref="autocompleteRef"
		:label="translate(label) ?? undefined"
		:name="name"
		:items="itemsTotal"
		:item-value="itemValue"
		:item-title="itemTitle"
		:return-object="returnObject"
		:append-inner-icon="icon"
		:loading="loading"
		:error-messages="errorMessage"
		:multiple="multiple"
		:disabled="isDisabled"
		:readonly="readOnly"
		:chips="chips"
		closable-chips
		:clearable="clearable"
		autocomplete="off"
		hide-selected
		:search="searchValue"
		:data-test="`${dataTestModule ? dataTestModule + '__' : ''}${(dataTest ?? name).replace(/-autocomplete$/, '')}-autocomplete`"
		:aria-busy="loading"
		:data-loading="loading ? 'true' : undefined"
		:custom-filter="customFilter"
		:menu-props="menuProps"
		v-bind="$attrs"
		@update:search="handleSearchUpdate"
		@update:model-value="handleModelUpdate"
		@focus="handleFocus"
		@blur="handleBlur"
	>
		<template v-if="$slots.chip || to" #chip="chipProps">
			<slot
				name="chip"
				v-bind="{
					...chipProps,
					chips,
					multiple,
					selected: fieldValue,
					to: handleTo(chipProps.item.raw),
				}"
			>
				<v-chip
					v-if="chips && !$slots.chip"
					v-bind="chipProps.props"
					:to="handleTo(chipProps.item.raw)"
				></v-chip>
				<router-link
					v-else-if="!chips && to && !$slots.chip"
					:to="handleTo(chipProps.item.raw)"
				>
					{{ chipProps.item.title }}
				</router-link>
			</slot>
		</template>
		<template #item="props" v-if="$slots.item">
			<slot name="item" v-bind="props" v-if="!loading">
				<v-list-item v-bind="props" :key="props.item.id"> </v-list-item>
			</slot>
		</template>
		<template #prepend-item>
			<slot v-if="$slots['prepend-item']" name="prepend-item"> </slot>
			<v-list-item v-else-if="search">
				<v-label class="pr-2">Suchbegriff</v-label>{{ search }}
				<template #append>
					<ButtonIcon
						v-if="search"
						:disabled="loading"
						size="small"
						icon="fal fa-xmark"
						notext
						color="error"
						@click="search = undefined"
					>
					</ButtonIcon>
				</template>
			</v-list-item>
			<v-divider></v-divider>
		</template>
		<template #append-item>
			<v-list-item
				v-if="!loading"
				data-test="data-loaded"
				:title="
					`${itemsTotal.length} / ${itemCount} ${translate(displayName)}` +
					' ' +
					translate('common.loaded')
				"
			>
				<template v-if="itemsTotal.length < itemCount" #append>
					<v-tooltip
						:text="
							translate({
								key: 'common.loadMore',
								params: {
									displayName: translate(displayName),
								},
							})
						"
					>
						<template #activator="{ props }">
							<v-btn
								v-bind="props"
								color="info"
								size="small"
								:icon="
									loading ? 'fal fa-sync fa-spin' : 'fal fa-cloud-download'
								"
								:disabled="loading"
								@click="loadNextPage"
							>
							</v-btn>
						</template>
					</v-tooltip>
				</template>
			</v-list-item>
		</template>
		<template #append v-if="$slots.append">
			<slot name="append"></slot>
		</template>
		<template #no-data>
			<v-list-item v-if="loading" data-test="no-data__loading">
				<template #prepend>
					<v-icon>fal fa-spinner fa-spin</v-icon>
				</template>
				<v-list-item-title>
					{{ translate('component.nodata.loading') }}
				</v-list-item-title>
			</v-list-item>
			<v-list-item
				v-else
				data-test="no-data"
				:title="titleNoData"
			></v-list-item>
		</template>
	</v-autocomplete>
</template>

<script setup lang="ts">
import { useTable } from '@/composables/useTable.ts'
import { useField } from 'vee-validate'
import { computed, nextTick, ref, toRef, watch } from 'vue'
import type { AutocompleteProps } from './autocomplete'
import { useTranslate } from 'composables/useTranslate.ts'
import type { FilterMatch } from 'vuetify/lib/composables/filter.d.ts'

type Props = { modelValue?: any } & AutocompleteProps

defineEmits(['update:modelValue'])

const { translate } = useTranslate()
const {
	to = undefined,
	uniqueKey = 'autocomplete',
	name = 'autocomplete',
	itemValue = 'id',
	itemTitle = 'id',
	displayName = '',
	label,
	rules,
	chips = undefined,
	multiple = false,
	disabled = false,
	readOnly = false,
	validateOnChange = true,
	preloadAllPages = false,
	returnObject = true,
	customFilterFields = [],
	customFilterMinQueryLength = 2,
	params = {
		od: 'ASC',
		of: 'hp_id',
	},
	modelValue,
	clearable = true,
	dataTest = undefined,
	dataTestModule = undefined,
	// menuProps war in autocomplete.d.ts zwar deklariert, wurde aber nie
	// destrukturiert und nie gebunden. Als deklariertes Prop landet es auch
	// nicht in $attrs -- ein :menu-props am Aufrufer war damit wirkungslos.
	menuProps = undefined,
} = defineProps<Props>()

const {
	value: fieldValue,
	errorMessage,
	resetField,
} = useField(
	toRef(() => name),
	toRef(() => rules),
	{
		validateOnValueUpdate: validateOnChange,
		label: translate(label) ?? undefined,
		syncVModel: true,
		initialValue: modelValue,
	}
)

const isDisabled = computed(() => disabled || false)
const localSearch = ref<string>('')
const isFocused = ref(false)
const suppressNextSearch = ref(false)
const lastSearchValue = ref<string>('')
const endpoint = defineModel<string>('endpoint', {
	required: true,
})

const {
	itemsTotal,
	itemsLoadedLength,
	itemsTotalLength,
	itemCount,
	isLoading: loading,
	handleSortChange,
	search,
	reload,
	page,
	tableId,
} = useTable({
	endpoint: computed(() => endpoint.value),
	name: uniqueKey,
	params,
	preloadAllPages,
	usePlaceHolderData: true,
})

function handleTo(item: any) {
	return typeof to === 'function' ? to(item) : to
}

const fieldValueGiven = computed(
	() =>
		(typeof fieldValue.value === 'object' && fieldValue.value !== null) ||
		(Array.isArray(fieldValue.value) && fieldValue.value.length > 0)
)

const searchValue = computed({
	get() {
		return localSearch.value
	},
	set(value) {
		localSearch.value = value || ''
	},
})

const titleNoData = computed(() => {
	if (loading.value) return translate('component.nodata.loading')
	return translate({
		key: 'component.nodata.noDataLoaded',
		params: {
			name: translate(displayName) ?? translate('common.data'),
		},
	})
})

const autocompleteRef = ref<InstanceType<typeof HTMLInputElement> | null>(null)

watch(loading, (isLoading, wasLoading) => {
	if (!isLoading && wasLoading) {
		suppressNextSearch.value = true
		setTimeout(() => {
			suppressNextSearch.value = false
		}, 200)
	}
})

watch(
	fieldValue,
	(newValue, oldValue) => {
		if (!newValue && oldValue) {
			suppressNextSearch.value = true
			localSearch.value = ''
			lastSearchValue.value = ''
			search.value = undefined

			setTimeout(() => {
				suppressNextSearch.value = false
			}, 100)
		}
	},
	{ deep: true }
)

watch(endpoint, (value, oldValue) => {
	if (value !== oldValue) {
		resetField({ value: undefined })
		localSearch.value = ''
		lastSearchValue.value = ''
	}
})

function getByPath(obj: any, path: string) {
	if (!obj || !path) return undefined

	if (!path.includes('.')) {
		return Object.prototype.hasOwnProperty.call(obj, path)
			? obj[path]
			: undefined
	}

	return path
		.split('.')
		.reduce((o: any, key: string) => (o && key in o ? o[key] : undefined), obj)
}

function resolveItemPath(item: any, path: string) {
	if (item == null) return undefined

	const direct = getByPath(item, path)
	if (direct !== undefined) return direct

	if (item.raw) {
		const fromRaw = getByPath(item.raw, path)
		if (fromRaw !== undefined) return fromRaw
	}

	return undefined
}

function normalizeValue(value: unknown) {
	if (value == null) return ''
	return String(value).trim().toLowerCase()
}

const customFilter = computed(() => {
	if (customFilterFields.length === 0) return undefined
	return (itemTitle: string, queryText: string, item: any): FilterMatch => {
		const rawQuery = (queryText ?? '').toString().trim()
		if (!rawQuery) return true

		if (
			customFilterMinQueryLength > 0 &&
			rawQuery.length < customFilterMinQueryLength
		)
			return true

		const query = normalizeValue(rawQuery)
		// vuetify default search (itemTitle)
		const title = normalizeValue(itemTitle ?? '')
		if (title.includes(query)) return true

		for (const path of customFilterFields) {
			const val = resolveItemPath(item, path)
			if (val == null) continue
			const s = normalizeValue(val)
			if (s.includes(query)) return true
		}
		return false
	}
})

const getDisplayText = (value: any): string => {
	if (!value) return ''

	if (Array.isArray(value)) {
		return ''
	}

	if (typeof value === 'object') {
		const displayValue =
			typeof itemTitle === 'function'
				? itemTitle(fieldValue.value)
				: value[itemTitle]
		return displayValue ? String(displayValue) : ''
	}

	return String(value)
}

const loadNextPage = async () => {
	page.value++
}

const handleSearchUpdate = (value: string | undefined) => {
	if (search.value && value === '') {
		search.value = undefined
	}
	if (suppressNextSearch.value) {
		localSearch.value = value || ''
		return
	}

	if (fieldValueGiven.value && value === getDisplayText(fieldValue.value)) {
		localSearch.value = value || ''
		return
	}

	localSearch.value = value || ''
	lastSearchValue.value = value || ''

	const shouldSearch =
		itemsLoadedLength.value < itemsTotalLength.value &&
		value?.trim() &&
		!loading.value &&
		(!fieldValueGiven.value ||
			getDisplayText(fieldValue.value) !== value.trim())
	if (shouldSearch && typeof value === 'string') {
		search.value = value.trim()
	}
}

const handleModelUpdate = (value: any) => {
	if (value) {
		suppressNextSearch.value = true

		nextTick(() => {
			localSearch.value = ''
			lastSearchValue.value = ''
			search.value = undefined

			// Bei Einfachauswahl schließt die Liste nach der Auswahl. Das frühere
			// focus() direkt nach blur() hat das Menü im selben Tick wieder geöffnet.
			// Bei multiple bleibt sie offen, damit weiter ausgewählt werden kann.
			autocompleteRef.value?.blur()
			if (multiple) autocompleteRef.value?.focus()

			setTimeout(() => {
				suppressNextSearch.value = false
			}, 300)
		})
	}
}

const handleFocus = () => {
	if (!disabled || !readOnly) return
	isFocused.value = true
	if (!multiple && fieldValueGiven.value) {
		const displayText = getDisplayText(fieldValue.value)
		if (displayText) {
			suppressNextSearch.value = true
			localSearch.value = displayText
			lastSearchValue.value = displayText

			setTimeout(() => {
				suppressNextSearch.value = false
			}, 100)
		}
	}
}

const handleBlur = () => {
	isFocused.value = false

	if (!fieldValueGiven.value || multiple) {
		nextTick(() => {
			suppressNextSearch.value = true
			localSearch.value = ''
			lastSearchValue.value = ''
			search.value = undefined

			setTimeout(() => {
				suppressNextSearch.value = false
			}, 100)
		})
	}
}

defineExpose({
	itemsTotal,
	reload,
	handleSortChange,
	loading,
})
</script>
